import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, TextField, MenuItem, Alert, CircularProgress, Stack, Divider } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const RunBillingPage = () => {
    const [structures, setStructures] = useState([]);
    const [selectedStructure, setSelectedStructure] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchStructures = async () => {
            try {
                const userInfo = localStorage.getItem('userInfo');
                const token = userInfo ? JSON.parse(userInfo).token : null;
                const { data } = await axios.get(getApiUrl('fees/structures'), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStructures(data);
            } catch (err) {
                console.error('Failed to load structures', err);
            }
        };
        fetchStructures();
    }, []);

    const handleRunBilling = async () => {
        if (!selectedStructure || !dueDate) {
            setError('Please select a fee structure and a due date.');
            return;
        }

        setLoading(true);
        setError('');
        setResult(null);

        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;

            const res = await axios.post(getApiUrl('fees/billing/run'), {
                feeStructureId: selectedStructure,
                dueDate
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setResult(res.data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Billing run failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h5" fontWeight={700} color="#1a237e" mb={4}>Auto-Billing Engine</Typography>

            <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: '1px solid #f0f0f0', maxWidth: 600 }}>
                <Typography variant="body1" color="text.secondary" mb={3}>
                    Select a Fee Structure template to automatically generate invoices for all eligible students.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                {result && (
                    <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ mb: 3 }}>
                        <Typography fontWeight="bold">Success!</Typography>
                        Billing run complete. Created {result.invoicesCreated} invoices. Skipped {result.skipped} (already billed).
                    </Alert>
                )}

                <Stack spacing={3}>
                    <TextField
                        select
                        label="Select Fee Structure"
                        fullWidth
                        value={selectedStructure}
                        onChange={(e) => {
                            setSelectedStructure(e.target.value);
                            setResult(null);
                        }}
                        helperText="Invoices will be generated for ALL students in this structure's Grade."
                    >
                        {structures.map((s: any) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.name} ({s.grade} - {s.term} {s.academicYear}) - ${Number(s.totalAmount).toLocaleString()}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        type="date"
                        label="Due Date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                    />

                    <Divider />

                    <Button
                        variant="contained"
                        size="large"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                        disabled={loading || !selectedStructure || !dueDate}
                        onClick={handleRunBilling}
                        sx={{ bgcolor: '#1a237e', py: 1.5 }}
                    >
                        {loading ? 'Processing...' : 'Run Auto-Billing'}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
};

export default RunBillingPage;
